import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search as SearchIcon, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import BlogCard from "@/components/blog/BlogCard";
import EmptyState from "@/components/common/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { blogsService } from "@/services/blogsService";
import { useSite } from "@/context/SiteDataContext";
import { FileSearch } from "lucide-react";

const PAGE_SIZE = 6;

export default function Blog() {
  const SITE = useSite();
  const [posts, setPosts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    blogsService.getBlogPosts().then(setPosts);
    blogsService.getBlogCategories().then((c) => setCategories(["All", ...c]));
  }, []);

  const featured = posts?.find((p) => p.featured);
  const filtered = useMemo(() => {
    if (!posts) return null;
    return posts.filter((p) => {
      if (activeCat !== "All" && p.category !== activeCat) return false;
      if (q.trim() && !`${p.title} ${p.excerpt}`.toLowerCase().includes(q.trim().toLowerCase())) return false;
      return true;
    });
  }, [posts, activeCat, q]);

  const paged = filtered?.slice(0, page * PAGE_SIZE);
  const hasMore = filtered && paged && paged.length < filtered.length;

  return (
    <>
      <Seo routeKey="blog" title="Blog" description="Laboratory insights, guides and articles from Athena Scientific." canonical={`https://${SITE.website}/blog`} />
      <PageHero eyebrow="Blogs" title="Laboratory insights & perspectives" description="Practical guides and perspectives on laboratory technologies, workflows and best practice." crumbs={[{ label: "Blogs" }]} />

      <section className="container-x py-16 md:py-20">
        {featured && !q && activeCat === "All" && (
          <Reveal>
            <Link to={`/blog/${featured.slug}`} data-testid="blog-featured" className="group grid lg:grid-cols-2 gap-8 items-center mb-16 rounded-lg overflow-hidden border border-border hover:shadow-[0_12px_32px_rgba(0,0,0,0.06)] transition-all">
              <div className="overflow-hidden aspect-[16/11] bg-[#f6f8f9]">
                <img src={featured.image} alt={featured.title} decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-8 lg:pr-12">
                <div className="flex items-center gap-3 text-xs uppercase tracking-widest font-semibold text-greyish">
                  <span className="text-aqua">Featured</span><span className="w-1 h-1 rounded-full bg-border" /><span>{featured.category}</span>
                </div>
                <h2 className="mt-4 text-2xl md:text-3xl font-bold tracking-tight text-ink group-hover:text-aqua transition-colors text-balance">{featured.title}</h2>
                <p className="mt-4 text-greyish">{featured.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-ink group-hover:gap-3 group-hover:text-aqua transition-all">Read Article <ArrowRight className="w-4 h-4" /></span>
              </div>
            </Link>
          </Reveal>
        )}

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button key={c} onClick={() => { setActiveCat(c); setPage(1); }} data-testid={`blog-cat-${c}`} className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${activeCat === c ? "bg-ink text-white" : "bg-[#f6f8f9] text-greyish hover:text-ink"}`}>{c}</button>
            ))}
          </div>
          <div className="relative max-w-xs w-full">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-greyish" />
            <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="blog-search" placeholder="Search articles…" className="w-full pl-10 pr-4 py-2.5 border border-border rounded-md text-sm outline-none focus:border-aqua focus:ring-2 focus:ring-aqua/20 transition-all" />
          </div>
        </div>

        {!paged ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">{Array.from({ length: 3 }).map((_, i) => <div key={i}><Skeleton className="aspect-[16/10] rounded-lg" /><Skeleton className="h-4 w-2/3 mt-4" /><Skeleton className="h-6 w-full mt-3" /></div>)}</div>
        ) : paged.length === 0 ? (
          <EmptyState icon={FileSearch} title="No articles found" description="Try a different category or search term." testId="blog-empty" />
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">{paged.map((p, i) => <BlogCard key={p.id} post={p} index={i} />)}</div>
            {hasMore && (
              <div className="mt-14 text-center">
                <button onClick={() => setPage((p) => p + 1)} data-testid="blog-load-more" className="inline-flex items-center gap-2 border border-border px-7 py-3.5 rounded-md font-semibold text-ink hover:bg-[#f6f8f9] transition-all">Load more articles</button>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}
